import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Server, 
  Activity, 
  Users, 
  Eye, 
  Tag, 
  Plus, 
  Trash2, 
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Globe,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MasterAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newInstance, setNewInstance] = useState({ name: '', url: '', api_key: '', notes: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, healthRes] = await Promise.all([
        axios.get(`${API}/api/master/stats`),
        axios.get(`${API}/api/master/health`)
      ]);
      setStats(statsRes.data);
      setHealth(healthRes.data);
    } catch (error) {
      console.error('Error loading master stats:', error);
      toast.error('Failed to load instance data');
    } finally {
      setLoading(false);
    }
  };

  const addInstance = async () => {
    if (!newInstance.name || !newInstance.url) {
      toast.error('Name and URL are required');
      return;
    }
    try {
      await axios.post(`${API}/api/master/instances`, newInstance);
      toast.success('Instance added successfully');
      setShowAddModal(false);
      setNewInstance({ name: '', url: '', api_key: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to add instance');
    }
  };

  const removeInstance = async (instanceId) => {
    if (!window.confirm('Are you sure you want to remove this instance?')) return;
    try {
      await axios.delete(`${API}/api/master/instances/${instanceId}`);
      toast.success('Instance removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove instance');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'unhealthy':
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'unreachable':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'unhealthy':
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'unreachable':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-primary" />
          <span className="text-lg">Loading instance data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/admin" className="p-2 hover:bg-white/20 rounded-full transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <Server className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">Master Admin Dashboard</h1>
              <p className="text-white/80 text-sm">Manage all ScanIt2Go instances</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Aggregate Stats */}
        {stats?.totals && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-border/40 bg-card p-6"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Server className="w-4 h-4" />
                <span className="text-sm">Instances</span>
              </div>
              <p className="text-3xl font-bold">{stats.totals.total_instances}</p>
              <p className="text-xs text-green-600 mt-1">{stats.totals.active_instances} active</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="rounded-2xl border border-border/40 bg-card p-6"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Tag className="w-4 h-4" />
                <span className="text-sm">Total Tags</span>
              </div>
              <p className="text-3xl font-bold">{stats.totals.total_tags?.toLocaleString()}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl border border-border/40 bg-card p-6"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Eye className="w-4 h-4" />
                <span className="text-sm">Total Scans</span>
              </div>
              <p className="text-3xl font-bold">{stats.totals.total_views?.toLocaleString()}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="rounded-2xl border border-border/40 bg-card p-6"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Users className="w-4 h-4" />
                <span className="text-sm">Total Clients</span>
              </div>
              <p className="text-3xl font-bold">{stats.totals.total_clients?.toLocaleString()}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="rounded-2xl border border-border/40 bg-card p-6"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Activity className="w-4 h-4" />
                <span className="text-sm">Health</span>
              </div>
              <p className={`text-lg font-bold ${health?.all_healthy ? 'text-green-600' : 'text-yellow-600'}`}>
                {health?.all_healthy ? 'All Healthy' : 'Issues Detected'}
              </p>
            </motion.div>
          </div>
        )}

        {/* Instance List */}
        <div className="rounded-2xl border border-border/40 bg-card overflow-hidden">
          <div className="p-6 border-b border-border/40 flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Registered Instances
            </h2>
            <div className="flex items-center gap-3">
              <button
                onClick={loadData}
                className="p-2 hover:bg-secondary rounded-full transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-opacity"
              >
                <Plus className="w-4 h-4" />
                Add Instance
              </button>
            </div>
          </div>

          <div className="divide-y divide-border/40">
            {stats?.instances?.map((instance, index) => {
              const healthInfo = health?.health_check?.find(h => h.instance_id === instance.instance_id);
              return (
                <motion.div
                  key={instance.instance_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-6 hover:bg-secondary/20 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getStatusIcon(healthInfo?.status || instance.status)}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{instance.instance_name}</h3>
                          {instance.instance_id === 'master' && (
                            <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded-full">
                              Master
                            </span>
                          )}
                        </div>
                        <a 
                          href={instance.instance_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-muted-foreground hover:text-primary"
                        >
                          {instance.instance_url}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Tags</p>
                        <p className="font-semibold">{instance.total_tags?.toLocaleString() || 0}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Scans</p>
                        <p className="font-semibold">{instance.total_views?.toLocaleString() || 0}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Clients</p>
                        <p className="font-semibold">{instance.total_clients?.toLocaleString() || 0}</p>
                      </div>
                      {healthInfo?.response_time_ms && (
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Latency</p>
                          <p className="font-semibold">{healthInfo.response_time_ms}ms</p>
                        </div>
                      )}
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(healthInfo?.status || instance.status)}`}>
                        {healthInfo?.status || instance.status}
                      </span>
                      {instance.instance_id !== 'master' && (
                        <button
                          onClick={() => removeInstance(instance.instance_id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                          title="Remove instance"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {instance.error && (
                    <p className="mt-2 text-sm text-red-500">{instance.error}</p>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Scaling Tips */}
        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <h3 className="font-semibold text-amber-800 mb-2">Scaling Tips</h3>
          <ul className="text-sm text-amber-700 space-y-1">
            <li>• Each instance can handle ~100 white-label clients efficiently</li>
            <li>• Clone this app on Emergent to create new instances when approaching capacity</li>
            <li>• Use geographic naming (US-East, EU-West) for better organization</li>
            <li>• All instances share the same Cloudflare R2 bucket for media storage</li>
          </ul>
        </div>
      </div>

      {/* Add Instance Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-background rounded-2xl shadow-xl max-w-md w-full p-6"
          >
            <h2 className="text-xl font-bold mb-4">Add New Instance</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Instance Name *</label>
                <input
                  type="text"
                  value={newInstance.name}
                  onChange={(e) => setNewInstance({ ...newInstance, name: e.target.value })}
                  placeholder="e.g., US East (Clients 101-200)"
                  className="w-full px-4 py-2 rounded-xl border border-border focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Instance URL *</label>
                <input
                  type="url"
                  value={newInstance.url}
                  onChange={(e) => setNewInstance({ ...newInstance, url: e.target.value })}
                  placeholder="https://us-east.scanit2go.com"
                  className="w-full px-4 py-2 rounded-xl border border-border focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">API Key (optional)</label>
                <input
                  type="password"
                  value={newInstance.api_key}
                  onChange={(e) => setNewInstance({ ...newInstance, api_key: e.target.value })}
                  placeholder="For authenticated requests"
                  className="w-full px-4 py-2 rounded-xl border border-border focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Notes (optional)</label>
                <textarea
                  value={newInstance.notes}
                  onChange={(e) => setNewInstance({ ...newInstance, notes: e.target.value })}
                  placeholder="Any notes about this instance..."
                  rows={2}
                  className="w-full px-4 py-2 rounded-xl border border-border focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 border border-border rounded-xl hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addInstance}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-xl hover:opacity-90 transition-opacity"
              >
                Add Instance
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MasterAdminDashboard;
